/*
  Nische hero - 3D logo watermark.

  The flat SVG watermark is what paints first and it is never removed. This only
  loads <model-viewer> once the hero is actually near the viewport, and only
  fades the 3D mark in once the model has genuinely loaded. If the script is
  blocked, WebGL is unavailable, or the GLB fails, the hero keeps the flat mark
  and nothing looks broken - the 3D is enhancement, never the thing the hero
  depends on.

  model-viewer is ~254KB, which is why it is nowhere near the critical path: the
  hero is where LCP is measured.

  Guarded: a hero can appear more than once on a page, and this file is emitted
  per section.
*/
if (!window.NischeHero3D) {
  window.NischeHero3D = true;

  // Kept in sync with the modulepreload hint in nische-hero.liquid - if this
  // version ever changes, update the URL there too.
  var NISCHE_MV_SRC = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js';
  var loading = false;

  function loadModelViewer() {
    if (loading || window.customElements.get('model-viewer')) return;
    loading = true;
    var s = document.createElement('script');
    s.type = 'module';
    s.src = NISCHE_MV_SRC;
    document.head.appendChild(s);
  }

  // Where the mark sits at rest: facing front, upright.
  var HOME_ORBIT = '0deg 90deg auto';
  // How long after release before the idle spin picks up again.
  var RESUME_AFTER = 900;

  function wire(mv) {
    if (mv.dataset.nischeWired) return;
    mv.dataset.nischeWired = '1';

    // The watermark is decorative and its wrapper is aria-hidden, so the model
    // must not sit in the tab order - model-viewer makes itself focusable when
    // camera-controls is on.
    mv.setAttribute('tabindex', '-1');

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      mv.removeAttribute('auto-rotate');
    }

    /*
      Return to front on release.

      The orbit is unclamped so the mark turns all the way over in both axes;
      this is what stops it being left face-down. Setting cameraOrbit does not
      snap - model-viewer eases to a new goal orbit, and interpolation-decay on
      the element sets how softly.

      The idle spin is switched off for the duration of a drag rather than left
      to model-viewer's auto-rotate-delay, so the spin and the glide home never
      fight over the same camera.
    */
    var spins = mv.hasAttribute('auto-rotate');
    var dragging = false;
    var resumeTimer = null;

    function release() {
      if (!dragging) return;
      dragging = false;
      document.documentElement.classList.remove('nische-rotating');
      mv.cameraOrbit = HOME_ORBIT;
      if (!spins) return;
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(function () {
        mv.setAttribute('auto-rotate', '');
      }, RESUME_AFTER);
    }

    mv.addEventListener('pointerdown', function () {
      dragging = true;
      // The hero itself is permanently unselectable; this covers a drag that
      // carries on past it.
      document.documentElement.classList.add('nische-rotating');
      clearTimeout(resumeTimer);
      if (spins) mv.removeAttribute('auto-rotate');
    });

    // Stops the browser starting a native drag from the canvas mid-rotate.
    mv.addEventListener('dragstart', function (evt) {
      evt.preventDefault();
    });

    // On the document, not the element: a drag very often ends with the pointer
    // outside the model, and a pointerup that never fires would leave the mark
    // stranded at whatever angle it was let go of.
    document.addEventListener('pointerup', release);
    document.addEventListener('pointercancel', release);

    mv.addEventListener(
      'load',
      function () {
        var colour = mv.dataset.colour;
        if (colour) {
          try {
            // setBaseColorFactor takes a CSS colour string, so the section's
            // existing colour setting drives the material with no conversion.
            mv.model.materials[0].pbrMetallicRoughness.setBaseColorFactor(colour);
          } catch (e) {
            /* keep the colour baked into the GLB */
          }
        }
        var wrap = mv.closest('.nische-hero__watermark');
        if (wrap) wrap.classList.add('is-3d');
      },
      { once: true }
    );
  }

  /*
    Runs cb once the first hero image has painted (or straight away if there
    is no image, or it has already loaded, or it fails). The hero image is the
    page's LCP element; model-viewer (~250KB) plus the GLB must not share the
    wire with it on a slow connection. The flat SVG mark is on screen the whole
    time, so all this changes is when the 3D fades in.
  */
  function afterHeroPaint(cb) {
    var img = document.querySelector('.nische-hero__image');
    if (!img || img.complete) {
      cb();
      return;
    }
    var done = false;
    function once() {
      if (done) return;
      done = true;
      cb();
    }
    img.addEventListener('load', once, { once: true });
    img.addEventListener('error', once, { once: true });
    // Safety net: never wait forever on a stalled image.
    setTimeout(once, 4000);
  }

  function init() {
    var models = document.querySelectorAll('.nische-hero__model');
    if (!models.length) return;

    models.forEach(wire);

    afterHeroPaint(function () {
      observe(models);
    });
  }

  function observe(models) {
    // rootMargin so the download starts just before the mark is reached rather
    // than after it is already on screen.
    var io = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          loadModelViewer();
          obs.unobserve(entry.target);
        });
      },
      { rootMargin: '300px' }
    );
    models.forEach(function (m) {
      io.observe(m);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor re-renders a section without reloading the page.
  document.addEventListener('shopify:section:load', init);
}
